import React, { useState, useRef, useEffect } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import { useClassesStore } from '../../stateManagers/store/useClassesStore';
import { useFeeTypesStore } from '../../stateManagers/store/useFeeTypesStore';
import { useFeeStructureStore } from '../../stateManagers/store/useFeeStructureStore';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../stateManagers/newStores/useSchoolDetailsStore';

// ── Inline SVG icon system (no font/CSS dependency) ───────────────────────────
const Ico = ({
  d, size = 16, style = {}, fill = false, viewBox = '0 0 24 24',
}: {
  d: string | string[]; size?: number; style?: React.CSSProperties; fill?: boolean; viewBox?: string;
}) => (
  <svg
    width={size} height={size} viewBox={viewBox}
    fill={fill ? 'currentColor' : 'none'}
    stroke={fill ? 'none' : 'currentColor'}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...style }}
  >
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoCalendar     = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z']} />;
const IcoGradCap      = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M22 10v6M2 10l10-5 10 5-10 5z', 'M6 12v5c3 3 9 3 12 0v-5']} />;
const IcoList         = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />;
const IcoUpload       = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />;
const IcoCloud        = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M18 8h1a4 4 0 010 8h-1M2 8a4 4 0 014-4h8a4 4 0 014 4v8a4 4 0 01-4 4H6a4 4 0 01-4-4z" />;
const IcoSliders      = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />;
const IcoTable        = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />;
const IcoWallet       = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M21 12V7H5a2 2 0 010-4h14v4M21 12v5H5a2 2 0 000 4h16v-5M21 12H9" />;
const IcoCalendarPlus = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z', 'M12 14v6M9 17h6']} />;
const IcoCheck        = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M20 6L9 17l-5-5" />;
const IcoCheckCircle  = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M22 11.08V12a10 10 0 11-5.93-9.14M22 4L12 14.01l-3-3" />;
const IcoX            = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M18 6L6 18M6 6l12 12" />;
const IcoWarn         = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01" />;
const IcoInfo         = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10zM12 8v4M12 16h.01" />;
const IcoPencil       = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />;
const IcoArrowRight   = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M5 12h14M12 5l7 7-7 7" />;
const IcoArrowLeft    = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M19 12H5M12 19l-7-7 7-7" />;
const IcoTrash        = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;
const IcoRefresh      = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.36-3.36L23 10M1 14l5.13 4.36A9 9 0 0020.49 15" />;
const IcoClone        = (p: { size?: number; style?: React.CSSProperties }) =>
  <Ico {...p} d={['M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2', 'M16 8h2a2 2 0 012 2v8a2 2 0 01-2 2h-8a2 2 0 01-2-2v-2']} />;
const IcoSpinner      = ({ size = 16, style = {} }: { size?: number; style?: React.CSSProperties }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"
    style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', animation: 'fss-spin 0.75s linear infinite', ...style }}>
    <path d="M12 2a10 10 0 0110 10" />
  </svg>
);

// ── Types ─────────────────────────────────────────────────────────────────────
/** IDs may be ObjectID hex strings from the new API */
type ID = string | number;

interface FeeType {
  id: ID;
  record_id?: string; // MongoDB _id of the fee-structure row
  fee_name: string;
  amount: number;
  optional: 0 | 1;
}
interface MonthlyFees {
  month: string;       // display "April 2025"
  monthYear: string;   // API key "2025-04"
  fees: FeeType[];
}
interface ClassFeeData {
  classId: ID;
  className: string;
  monthlyFees: MonthlyFees[];
}
interface BulkRow {
  id: string;
  classId: ID;
  className: string;
  session: string;
  month: string;
  year: string;
  monthYear: string;
  fees: Record<string, number>; // fee_name → amount
}
interface BulkResult {
  status: string;
  message: string;
  row?: number;
  fee_name?: string;
  class_name?: string;
  month_year?: string;
  amount?: number;
  action?: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const months = MONTH_NAMES;
const uid = () => Math.random().toString(36).slice(2, 9);

const MONTH_FROM_NUM: Record<number, string> = {
  1: 'January', 2: 'February', 3: 'March', 4: 'April',
  5: 'May', 6: 'June', 7: 'July', 8: 'August',
  9: 'September', 10: 'October', 11: 'November', 12: 'December',
};

/** "2025-26" | "2025-2026" → "2025-2026" */
const normalizeSession = (session: string): string => {
  if (!session) return '';
  const parts = session.split('-');
  if (parts.length !== 2) return session;
  const start = parseInt(parts[0], 10);
  let end = parseInt(parts[1], 10);
  if (isNaN(start) || isNaN(end)) return session;
  if (end < 100) end = Math.floor(start / 100) * 100 + end;
  return `${start}-${end}`;
};

/** month name + year → "YYYY-MM" */
const toMonthYear = (monthName: string, year: number | string): string => {
  const mi = months.indexOf(monthName) + 1;
  return `${year}-${String(mi).padStart(2, '0')}`;
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const generateMonthsForSession = (opts: {
  session: string;
  durationStart: string;
  durationEnd: string;
}): { month: string; year: number }[] => {
  const { session, durationStart, durationEnd } = opts;
  if (!session || !durationStart || !durationEnd) return [];

  const normalized = normalizeSession(session);
  const parts = normalized.split('-').map(y => parseInt(y, 10));
  if (parts.length !== 2 || parts.some(isNaN)) return [];
  const [startYear, endYear] = parts;

  const si = months.indexOf(durationStart);
  const ei = months.indexOf(durationEnd);
  if (si === -1 || ei === -1) return [];

  const result: { month: string; year: number }[] = [];
  let mi = si;
  let yr = startYear;

  while (true) {
    result.push({ month: months[mi], year: yr });
    if (mi === ei && yr === endYear) break;
    mi++;
    if (mi > 11) { mi = 0; yr++; }
    if (result.length > 24) break;
  }
  return result;
};

/** Group flat API rows into { [monthYear]: [{ fee_type_id, amount, record_id }] } */
const groupFeeStructures = (
  rows: Array<{
    id?: string;
    _id?: string;
    fee_type_id: string;
    amount: number;
    month_year: string;
  }>
): Record<string, { fee_type_id: string; amount: number; record_id?: string }[]> => {
  const map: Record<string, { fee_type_id: string; amount: number; record_id?: string }[]> = {};
  for (const r of rows) {
    const key = r.month_year;
    if (!map[key]) map[key] = [];
    map[key].push({
      fee_type_id: String(r.fee_type_id),
      amount: r.amount,
      record_id: r.id || r._id,
    });
  }
  return map;
};

const buildClassFees = (
  classes: { id: ID; class_name: string }[],
  sessionMonths: { month: string; year: number }[],
  feeTypes: { id: ID; fee_name: string; status: string; optional: 0 | 1 }[],
  feeStructures: Record<string, { fee_type_id: string; amount: number; record_id?: string }[]>,
): ClassFeeData[] =>
  classes.map(cls => ({
    classId: cls.id,
    className: cls.class_name,
    monthlyFees: sessionMonths.map(sm => {
      const key = toMonthYear(sm.month, sm.year);
      const existing = feeStructures[key] ?? [];
      return {
        month: `${sm.month} ${sm.year}`,
        monthYear: key,
        fees: feeTypes
          .filter(ft => ft.status === 'active')
          .map(ft => {
            const ef = existing.find(e => String(e.fee_type_id) === String(ft.id));
            return {
              id: ft.id,
              record_id: ef?.record_id,
              fee_name: ft.fee_name,
              amount: ef ? ef.amount : 0,
              optional: ft.optional,
            };
          }),
      };
    }),
  }));

// ── Styles (unchanged) ────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
  .fss-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
  @keyframes fss-fadeSlide { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fss-fadeUp    { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fss-spin      { to { transform: rotate(360deg); } }
  .fss-root .p-chip { font-size: 12px !important; padding: 6px 12px !important; height: 32px !important; }
  .fss-mode-tab {
    padding: 8px 20px; border-radius: 10px; cursor: pointer;
    font-size: 13px; font-weight: 700; border: none; outline: none;
    transition: all 0.15s; display: flex; align-items: center; gap: 7px;
    font-family: 'DM Sans', sans-serif;
  }
  .fss-mode-tab.active   { background: white; color: #1e3a8a; box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
  .fss-mode-tab.inactive { background: transparent; color: rgba(255,255,255,0.7); }
  .fss-mode-tab.inactive:hover { color: white; background: rgba(255,255,255,0.1); }
  .fss-bulk-card {
    background: white; border-radius: 16px; padding: 24px; margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    border: 1.5px solid #e8edf5; animation: fss-fadeUp 0.3s ease both;
  }
  .fss-bulk-card-title {
    font-size: 14px; font-weight: 700; color: #1e293b;
    margin: 0 0 16px 0; display: flex; align-items: center; gap: 10px;
  }
  .fss-bicon {
    width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, #2855ae, #3b82f6); color: white;
  }
  .fss-bcls-chip {
    padding: 6px 13px; border-radius: 10px; cursor: pointer;
    font-size: 12px; font-weight: 600; transition: all 0.15s;
    border: 1.5px solid #e2e8f0; background: #f8fafc; color: #475569; user-select: none;
  }
  .fss-bcls-chip:hover  { border-color: #93c5fd; color: #1e40af; background: #eff6ff; }
  .fss-bcls-chip.active { border-color: #2855ae; background: #dbeafe; color: #1e40af; }
  .fss-bmonth-chip {
    padding: 5px 11px; border-radius: 8px; cursor: pointer;
    font-size: 11px; font-weight: 600; transition: all 0.15s;
    border: 1.5px solid #e2e8f0; background: #f8fafc; color: #64748b; user-select: none;
  }
  .fss-bmonth-chip:hover  { border-color: #a5b4fc; color: #4338ca; }
  .fss-bmonth-chip.active { border-color: #4f46e5; background: #eef2ff; color: #4338ca; }
  .fss-scope-toggle { display: flex; border-radius: 10px; overflow: hidden; border: 1.5px solid #e2e8f0; width: fit-content; }
  .fss-scope-btn {
    padding: 7px 16px; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.15s; border: none; outline: none;
    background: white; color: #64748b; font-family: 'DM Sans', sans-serif;
  }
  .fss-scope-btn.active { background: #1e3a8a; color: white; }
  .fss-bfee-row {
    display: flex; align-items: center; gap: 12px;
    padding: 11px 14px; border-radius: 11px; margin-bottom: 8px;
    background: #f8fafc; border: 1.5px solid #e8edf5;
  }
  .fss-preview-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
  .fss-preview-tbl th {
    background: #f1f5f9; padding: 9px 11px; text-align: left;
    font-weight: 700; color: #475569; font-size: 11px;
    border-bottom: 1.5px solid #e2e8f0; white-space: nowrap;
  }
  .fss-preview-tbl td { padding: 8px 11px; border-bottom: 1px solid #f1f5f9; color: #334155; }
  .fss-preview-tbl tr:last-child td { border-bottom: none; }
  .fss-preview-tbl tr:hover td { background: #f8fafc; }
  .fss-btn-primary {
    background: linear-gradient(135deg, #1e3a8a, #2563eb);
    color: white; border: none; border-radius: 11px;
    padding: 10px 22px; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: all 0.15s; display: flex; align-items: center; gap: 8px;
    box-shadow: 0 2px 8px rgba(37,99,235,0.25); font-family: 'DM Sans', sans-serif;
  }
  .fss-btn-primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(37,99,235,0.35); }
  .fss-btn-primary:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; transform: none; }
  .fss-btn-secondary {
    background: white; color: #475569; border: 1.5px solid #e2e8f0;
    border-radius: 11px; padding: 10px 22px; font-size: 13px; font-weight: 700;
    cursor: pointer; transition: all 0.15s; display: flex; align-items: center; gap: 8px;
    font-family: 'DM Sans', sans-serif;
  }
  .fss-btn-secondary:hover { border-color: #93c5fd; color: #1e40af; background: #eff6ff; }
  .fss-bresult-item {
    display: flex; align-items: flex-start; gap: 9px;
    padding: 9px 13px; border-radius: 9px; margin-bottom: 5px; font-size: 12px;
  }
  .fss-bresult-item.success { background: #f0fdf4; border: 1px solid #bbf7d0; }
  .fss-bresult-item.error   { background: #fff1f2; border: 1px solid #fecdd3; }
`;

// ── Component ─────────────────────────────────────────────────────────────────
export default function FeeStructureSetup() {
  const { classes, loading: classesLoading, fetchClasses } = useClassesStore();
  const { feeTypes, loading: feeTypesLoading } = useFeeTypesStore();
  const { hasData: hasExistingData } = useFeeStructureStore();
  const { schoolDetails, loading: schoolLoading } = useSchoolDetailsStore();

  // ── School / session context (required by all fee-structure APIs) ──
  const schoolId = schoolDetails?.school_id ?? '';
  const currentSession = schoolDetails?.current_session ?? null;
  const sessionId = currentSession?.id ?? '';
  const displaySession = currentSession?.session ?? '';
  const sessionStr = currentSession ? normalizeSession(currentSession.session) : '';
  const durationStart = currentSession?.start_month
    ? (MONTH_FROM_NUM[currentSession.start_month] ?? '')
    : '';
  const durationEnd = currentSession?.end_month
    ? (MONTH_FROM_NUM[currentSession.end_month] ?? '')
    : '';

  const [selectedClassId, setSelectedClassId]   = useState<ID | null>(null);
  const [currentClassData, setCurrentClassData] = useState<ClassFeeData | null>(null);
  const [classFees, setClassFees]               = useState<ClassFeeData[]>([]);
  const [saving, setSaving]                     = useState(false);
  const [uniformFees, setUniformFees]           = useState(false);
  const [sessionMonths, setSessionMonths]       = useState<{ month: string; year: number }[]>([]);
  const [localLoading, setLocalLoading]         = useState(false);
  const [mode, setMode]                         = useState<'manual' | 'bulk'>('manual');

  // ── Bulk wizard ──
  const [bulkStep, setBulkStep]                         = useState(0);
  const [bulkSelectedClasses, setBulkSelectedClasses]   = useState<string[]>([]);
  const [bulkMonthScope, setBulkMonthScope]             = useState<'ALL' | 'specific'>('ALL');
  const [bulkSelectedMonths, setBulkSelectedMonths]     = useState<string[]>([]);
  const [bulkYearScope, setBulkYearScope]               = useState<'ALL' | 'specific'>('ALL');
  const [bulkSpecificYear, setBulkSpecificYear]         = useState('');
  const [bulkUniformFees, setBulkUniformFees]           = useState(true);
  const [bulkUniformAmounts, setBulkUniformAmounts]     = useState<Record<string, number>>({});
  const [bulkPerClassAmounts, setBulkPerClassAmounts]   = useState<Record<string, Record<string, number>>>({});
  const [bulkRows, setBulkRows]                         = useState<BulkRow[]>([]);
  const [bulkUploading, setBulkUploading]               = useState(false);
  const [bulkResults, setBulkResults]                   = useState<BulkResult[]>([]);

  const toast = useRef<Toast>(null);
  const [isEditing, setIsEditing]         = useState(false);
  const [editSaving, setEditSaving]       = useState(false);
  const [deletingMonth, setDeletingMonth] = useState<string | null>(null);
  const [deletingAll, setDeletingAll]     = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (sessionStr && durationStart && durationEnd) {
      setSessionMonths(generateMonthsForSession({ session: sessionStr, durationStart, durationEnd }));
    } else {
      setSessionMonths([]);
    }
  }, [sessionStr, durationStart, durationEnd]);

  useEffect(() => {
    if (classes?.length && sessionMonths.length && feeTypes.length) {
      setClassFees(buildClassFees(classes, sessionMonths, feeTypes, {}));
    }
  }, [classes, sessionMonths, feeTypes]);

  useEffect(() => {
    if (feeTypes.length) {
      const init: Record<string, number> = {};
      feeTypes.filter(f => f.status === 'active').forEach(f => { init[f.fee_name] = 0; });
      setBulkUniformAmounts(init);
    }
  }, [feeTypes]);

  useEffect(() => {
    if (!selectedClassId && classes?.length && sessionMonths.length && feeTypes.length && schoolId && sessionId) {
      loadClassData(classes[0].id);
    }
  }, [classes, sessionMonths, feeTypes, schoolId, sessionId]);

  useEffect(() => {
    if (selectedClassId === null) { setCurrentClassData(null); return; }
    if (localLoading) return;
    const found = classFees.find(c => String(c.classId) === String(selectedClassId));
    if (found) setCurrentClassData(found);
  }, [classFees, selectedClassId, localLoading]);

  useEffect(() => {
    if (hasExistingData) setUniformFees(false);
  }, [hasExistingData]);

  // ── Load fee structures for one class via GET /api/fee-structures ─────────
  const loadClassData = async (classId: ID) => {
    if (!schoolId || !sessionId) return;

    setUniformFees(false);
    setIsEditing(false);
    setLocalLoading(true);
    try {
      const params = new URLSearchParams({
        school_id: schoolId,
        session_id: sessionId,
        class_id: String(classId),
        limit: '500',
      });
      const response = await apiClient.get(`/api/fee-structures?${params.toString()}`);
      console.log('[loadClassData] response ←', response.data);

      const rows: any[] = response.data?.data ?? [];
      const normalized = groupFeeStructures(rows);
      const hasData = Object.keys(normalized).length > 0;

      useFeeStructureStore.setState({
        feeStructures: normalized as any,
        hasData,
        loading: false,
        className: rows[0]?.class_name ?? null,
        session: displaySession || sessionStr,
      });

      const currentClasses  = useClassesStore.getState().classes;
      const currentFeeTypes = useFeeTypesStore.getState().feeTypes;
      const freshMonths = generateMonthsForSession({
        session: sessionStr,
        durationStart,
        durationEnd,
      });
      const activeMonths = freshMonths.length > 0 ? freshMonths : sessionMonths;
      const targetClass = (currentClasses.length ? currentClasses : []).find(
        (c: any) => String(c.id) === String(classId)
      );

      if (targetClass) {
        const rebuilt: ClassFeeData = {
          classId: targetClass.id,
          className: targetClass.class_name,
          monthlyFees: activeMonths.map(sm => {
            const key = toMonthYear(sm.month, sm.year);
            const existing = normalized[key] ?? [];
            return {
              month: `${sm.month} ${sm.year}`,
              monthYear: key,
              fees: currentFeeTypes
                .filter((ft: any) => ft.status === 'active')
                .map((ft: any) => {
                  const ef = existing.find(e => String(e.fee_type_id) === String(ft.id));
                  return {
                    id: ft.id,
                    record_id: ef?.record_id,
                    fee_name: ft.fee_name,
                    amount: ef ? ef.amount : 0,
                    optional: ft.optional,
                  };
                }),
            };
          }),
        };
        setClassFees(prev => {
          const exists = prev.some(c => String(c.classId) === String(classId));
          return exists
            ? prev.map(c => (String(c.classId) === String(classId) ? rebuilt : c))
            : [...prev, rebuilt];
        });
        setCurrentClassData(rebuilt);
      }
      setSelectedClassId(classId);
    } catch (err) {
      console.error('[loadClassData]', err);
      useFeeStructureStore.setState({ feeStructures: {}, hasData: false, loading: false });
      setSelectedClassId(classId);
    } finally {
      setLocalLoading(false);
    }
  };

  // ── Edit: PUT /api/fee-structures/bulk-update  +  POST bulk for new rows ──
  const handleEditSave = async () => {
    if (!selectedClassId || !currentClassData || !schoolId || !sessionId) return;
    setEditSaving(true);
    try {
      const putRows: { id: string; amount: number }[] = [];
      const insertEntries: {
        fee_type_id: string;
        class_id: string;
        month_year: string;
        amount: number;
      }[] = [];

      currentClassData.monthlyFees.forEach(mf => {
        mf.fees.forEach(f => {
          if (f.record_id) {
            putRows.push({ id: f.record_id, amount: f.amount });
          } else if (f.amount > 0 || true) {
            // include zero amounts so structure is complete; backend accepts amount: 0
            insertEntries.push({
              fee_type_id: String(f.id),
              class_id: String(selectedClassId),
              month_year: mf.monthYear,
              amount: f.amount,
            });
          }
        });
      });

      if (putRows.length > 0) {
        await apiClient.put('/api/fee-structures/bulk-update', putRows);
      }
      if (insertEntries.length > 0) {
        await apiClient.post('/api/fee-structures/bulk', {
          school_id: schoolId,
          session_id: sessionId,
          session: displaySession || sessionStr,
          entries: insertEntries,
        });
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Updated',
        detail: `Fee structure updated for ${currentClassData.className}`,
        life: 3000,
      });
      setIsEditing(false);
      await loadClassData(selectedClassId);
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Update failed',
        life: 4000,
      });
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete month: DELETE /api/fee-structures/bulk-delete ──────────────────
  const handleDeleteMonth = async (monthStr: string, monthYear: string) => {
    if (!selectedClassId || !sessionId) return;
    setDeletingMonth(monthStr);
    try {
      const res = await apiClient.delete('/api/fee-structures/bulk-delete', {
        data: {
          class_id: String(selectedClassId),
          session_id: sessionId,
          month_year: monthYear,
        },
      });
      if (res.data.status === 'success' || res.data.status === 'completed') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: `${monthStr} fees deleted (${res.data.deleted ?? 0})`,
          life: 3000,
        });
        await loadClassData(selectedClassId);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Delete failed',
        life: 4000,
      });
    } finally {
      setDeletingMonth(null);
    }
  };

  // ── Delete all for class+session ──────────────────────────────────────────
  const handleDeleteAll = async () => {
    if (!selectedClassId || !sessionId) return;
    setDeletingAll(true);
    try {
      const res = await apiClient.delete('/api/fee-structures/bulk-delete', {
        data: {
          class_id: String(selectedClassId),
          session_id: sessionId,
        },
      });
      if (res.data.status === 'success' || res.data.status === 'completed') {
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: `All fee structures deleted for ${currentClassData?.className}`,
          life: 3000,
        });
        setIsEditing(false);
        await loadClassData(selectedClassId);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Delete failed',
        life: 4000,
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleClassSelect = (classId: ID) => {
    if (String(classId) === String(selectedClassId)) return;
    loadClassData(classId);
  };

  const updateFeeAmount = (monthIndex: number, feeIndex: number, newAmount: number | null) => {
    if (!currentClassData || newAmount === null || (hasExistingData && !isEditing)) return;
    const applyUpdate = (cls: ClassFeeData): ClassFeeData => ({
      ...cls,
      monthlyFees: cls.monthlyFees.map((mf, mIdx) =>
        uniformFees
          ? { ...mf, fees: mf.fees.map((f, fIdx) => (fIdx === feeIndex ? { ...f, amount: newAmount } : f)) }
          : mIdx === monthIndex
          ? { ...mf, fees: mf.fees.map((f, fIdx) => (fIdx === feeIndex ? { ...f, amount: newAmount } : f)) }
          : mf
      ),
    });
    const updated = applyUpdate(currentClassData);
    setCurrentClassData(updated);
    setClassFees(prev =>
      prev.map(cls => (String(cls.classId) === String(selectedClassId) ? updated : cls))
    );
  };

  // ── Initial save: POST /api/fee-structures/bulk ───────────────────────────
  const handleSaveFees = async () => {
    if (!selectedClassId || !currentClassData || !schoolId || !sessionId || hasExistingData) return;
    try {
      setSaving(true);
      const entries: {
        fee_type_id: string;
        class_id: string;
        month_year: string;
        amount: number;
      }[] = [];

      currentClassData.monthlyFees.forEach(mf => {
        mf.fees.forEach(f => {
          entries.push({
            fee_type_id: String(f.id),
            class_id: String(selectedClassId),
            month_year: mf.monthYear,
            amount: f.amount,
          });
        });
      });

      const payload = {
        school_id: schoolId,
        session_id: sessionId,
        session: displaySession || sessionStr,
        entries,
      };
      console.log('[handleSaveFees] payload →', payload);
      const response = await apiClient.post('/api/fee-structures/bulk', payload);
      console.log('[handleSaveFees] response ←', response.data);

      if (response.data.status === 'success' || response.data.status === 'partial' || response.data.status === 'completed') {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: `Fee structure saved for ${currentClassData.className}`,
          life: 4000,
        });
        await loadClassData(selectedClassId);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.data.message || 'Save failed',
          life: 4000,
        });
      }
    } catch (error: any) {
      console.error('[handleSaveFees]', error.response?.data ?? error.message);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.message || 'Failed to save fees',
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Bulk helpers ──────────────────────────────────────────────────────────
  const bulkToggleClass = (name: string) =>
    setBulkSelectedClasses(p => (p.includes(name) ? p.filter(c => c !== name) : [...p, name]));
  const bulkToggleMonth = (m: string) =>
    setBulkSelectedMonths(p => (p.includes(m) ? p.filter(x => x !== m) : [...p, m]));

  const bulkBuildRows = () => {
    const monthsList: { month: string; year: string; monthYear: string }[] =
      bulkMonthScope === 'ALL'
        ? sessionMonths.map(sm => ({
            month: sm.month,
            year: String(sm.year),
            monthYear: toMonthYear(sm.month, sm.year),
          }))
        : bulkSelectedMonths.map(label => {
            const parts = label.split(' ');
            const m = parts[0];
            const y = parts[1] ?? bulkSpecificYear;
            return { month: m, year: y, monthYear: toMonthYear(m, y) };
          });

    const newRows: BulkRow[] = [];
    bulkSelectedClasses.forEach(clsName => {
      const cls = (classes || []).find((c: any) => c.class_name === clsName);
      if (!cls) return;
      monthsList.forEach(({ month, year, monthYear }) => {
        const yr = bulkYearScope === 'specific' ? bulkSpecificYear : year;
        newRows.push({
          id: uid(),
          classId: cls.id,
          className: clsName,
          session: displaySession || sessionStr,
          month,
          year: yr,
          monthYear: bulkYearScope === 'specific' ? toMonthYear(month, yr) : monthYear,
          fees: bulkUniformFees
            ? { ...bulkUniformAmounts }
            : { ...(bulkPerClassAmounts[clsName] || bulkUniformAmounts) },
        });
      });
    });
    setBulkRows(newRows);
    setBulkStep(1);
  };

  const bulkApplyAndPreview = () => {
    setBulkRows(p =>
      p.map(r => ({
        ...r,
        fees: bulkUniformFees
          ? { ...bulkUniformAmounts }
          : { ...(bulkPerClassAmounts[r.className] || bulkUniformAmounts) },
      }))
    );
    setBulkStep(2);
  };

  /** Build entries for POST /api/fee-structures/bulk */
  const bulkBuildEntries = (rows: BulkRow[]) => {
    const entries: {
      fee_type_id: string;
      class_id: string;
      month_year: string;
      amount: number;
    }[] = [];
    const activeFeeTypes = feeTypes.filter((ft: any) => ft.status === 'active');

    rows.forEach(r => {
      activeFeeTypes.forEach((ft: any) => {
        entries.push({
          fee_type_id: String(ft.id),
          class_id: String(r.classId),
          month_year: r.monthYear,
          amount: r.fees[ft.fee_name] ?? 0,
        });
      });
    });
    return entries;
  };

  const handleBulkUpload = async () => {
    if (!schoolId || !sessionId) {
      toast.current?.show({
        severity: 'error',
        summary: 'Missing context',
        detail: 'School or session not loaded',
        life: 4000,
      });
      return;
    }
    setBulkUploading(true);
    try {
      const entries = bulkBuildEntries(bulkRows);
      const payload = {
        school_id: schoolId,
        session_id: sessionId,
        session: displaySession || sessionStr,
        entries,
      };
      console.log('[handleBulkUpload] payload →', JSON.stringify(payload, null, 2));
      const res = await apiClient.post('/api/fee-structures/bulk', payload);
      console.log('[handleBulkUpload] response ←', res.data);
      setBulkResults(res.data.results || []);
      setBulkStep(3);
    } catch (err: any) {
      console.error('[handleBulkUpload]', err.response?.data ?? err.message);
      toast.current?.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: err.response?.data?.message || err.message,
        life: 4000,
      });
    } finally {
      setBulkUploading(false);
    }
  };

  const resetBulk = () => {
    setBulkStep(0);
    setBulkRows([]);
    setBulkResults([]);
    setBulkSelectedClasses([]);
    setBulkSelectedMonths([]);
    setBulkMonthScope('ALL');
    setBulkYearScope('ALL');
    setBulkSpecificYear('');
    setBulkUniformFees(true);
  };

  const totalFee =
    currentClassData?.monthlyFees.reduce(
      (sum, mf) => sum + mf.fees.reduce((s, f) => s + f.amount, 0),
      0
    ) || 0;

  const isLoadingInitial = classesLoading || schoolLoading || feeTypesLoading;
  const contextReady = Boolean(schoolId && sessionId);

  const manualCard: React.CSSProperties = {
    background: 'white',
    borderRadius: 16,
    padding: 20,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '2px solid #dbeafe',
    animation: 'fss-fadeSlide 0.5s ease-out',
  };

  return (
    <div className="fss-root" style={{ minHeight: '100vh', padding: 0, background: '#fdfdfd' }}>
      <style>{styles}</style>
      <Toast ref={toast} />

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        borderRadius: 24, padding: 32, marginBottom: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 16, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IcoCalendar size={20} style={{ color: 'white' }} />
          </div>
          <div>
            <h1 style={{ color: 'white', fontSize: 28, fontWeight: 600, margin: '0 0 4px 0' }}>Monthly Fee Setup</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ background: 'rgba(74,222,128,0.9)', color: 'white', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                {classes?.length || 0} Classes
              </span>
              {displaySession && (
                <span style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '4px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                  Session: {displaySession}
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.15)', borderRadius: 12, padding: 4 }}>
          <button className={`fss-mode-tab ${mode === 'manual' ? 'active' : 'inactive'}`} onClick={() => setMode('manual')}>
            <IcoList size={13} /> Manual Setup
          </button>
          <button className={`fss-mode-tab ${mode === 'bulk' ? 'active' : 'inactive'}`} onClick={() => setMode('bulk')}>
            <IcoUpload size={13} /> Bulk Upload
          </button>
        </div>
      </div>

      {/* Missing school/session context */}
      {!isLoadingInitial && !contextReady && (
        <div style={{ ...manualCard, textAlign: 'center', padding: 40 }}>
          <IcoWarn size={32} style={{ color: '#f59e0b', display: 'block', margin: '0 auto 12px' }} />
          <p style={{ color: '#92400e', fontSize: 14, fontWeight: 600 }}>
            School or current session not loaded. Please ensure school details are available.
          </p>
        </div>
      )}

      {/* ── Manual Mode ── */}
      {mode === 'manual' && isLoadingInitial && (
        <div style={{ ...manualCard, textAlign: 'center', padding: 40 }}>
          <IcoSpinner size={48} style={{ color: '#2855ae', display: 'block', margin: '0 auto' }} />
          <p style={{ color: '#6b7280', marginTop: 16, fontSize: 14 }}>Loading fee structure...</p>
        </div>
      )}

      {mode === 'manual' && !isLoadingInitial && contextReady && classes?.length > 0 && (
        <>
          <div style={{ ...manualCard, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: 10, padding: 8, minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IcoGradCap size={14} style={{ color: 'white' }} />
              </div>
              <h4 style={{ color: '#2855ae', fontSize: 14, fontWeight: 600, margin: 0 }}>Select Class</h4>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {classes.map((cls: any) => (
                <Chip
                  key={String(cls.id)}
                  label={cls.class_name}
                  onClick={() => handleClassSelect(cls.id)}
                  style={{
                    background: String(selectedClassId) === String(cls.id) ? '#e0f2fe' : '#f3f4f6',
                    color: String(selectedClassId) === String(cls.id) ? '#0369a1' : '#4b5563',
                    fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 12,
                    border: String(selectedClassId) === String(cls.id) ? '2px solid #2855ae' : '1px solid #e5e7eb',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
          </div>

          {selectedClassId && currentClassData && !localLoading && (
            <>
              {hasExistingData ? (
                <div style={{
                  background: isEditing ? '#fef3c7' : '#eff6ff',
                  border: `1px solid ${isEditing ? '#fbbf24' : '#93c5fd'}`,
                  borderRadius: 12, padding: '12px 16px', marginBottom: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isEditing
                      ? <IcoWarn size={16} style={{ color: '#f59e0b' }} />
                      : <IcoInfo size={16} style={{ color: '#3b82f6' }} />}
                    <span style={{ color: isEditing ? '#92400e' : '#1e40af', fontSize: 13, fontWeight: 500 }}>
                      {isEditing ? 'Editing mode — modify amounts then save or cancel.' : 'Existing fee structure loaded.'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => { setIsEditing(false); loadClassData(selectedClassId!); }}
                          style={{ background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#475569', fontWeight: 600, fontSize: 12 }}
                        >
                          <IcoX size={13} /> Cancel
                        </button>
                        <button
                          onClick={handleEditSave}
                          disabled={editSaving}
                          style={{ background: 'linear-gradient(135deg,#059669,#10b981)', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: editSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'white', fontWeight: 600, fontSize: 12, opacity: editSaving ? 0.7 : 1 }}
                        >
                          {editSaving ? <IcoSpinner size={13} /> : <IcoCheck size={13} style={{ color: 'white' }} />}
                          {editSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          onClick={handleDeleteAll}
                          disabled={deletingAll}
                          style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: deletingAll ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'white', fontWeight: 600, fontSize: 12, opacity: deletingAll ? 0.7 : 1 }}
                        >
                          {deletingAll ? <IcoSpinner size={13} /> : <IcoTrash size={13} style={{ color: 'white' }} />}
                          {deletingAll ? 'Deleting...' : 'Delete All'}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setIsEditing(true)}
                        style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'white', fontWeight: 600, fontSize: 12, boxShadow: '0 2px 8px rgba(59,130,246,0.3)' }}
                      >
                        <IcoPencil size={13} style={{ color: 'white' }} /> Edit
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IcoWarn size={16} style={{ color: '#f59e0b' }} />
                  <span style={{ color: '#92400e', fontSize: 13, fontWeight: 500 }}>
                    No fee structure found for this class. Add new fee structure below.
                  </span>
                </div>
              )}

              <div style={{ ...manualCard, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="checkbox"
                  id="uniformFees"
                  checked={uniformFees}
                  onChange={e => setUniformFees(e.target.checked)}
                  disabled={hasExistingData && !isEditing}
                  style={{
                    width: 18, height: 18,
                    cursor: hasExistingData && !isEditing ? 'not-allowed' : 'pointer',
                    accentColor: '#2855ae',
                    opacity: hasExistingData && !isEditing ? 0.5 : 1,
                  }}
                />
                <label
                  htmlFor="uniformFees"
                  style={{
                    color: hasExistingData && !isEditing ? '#9ca3af' : '#374151',
                    fontSize: 14, fontWeight: 600,
                    cursor: hasExistingData && !isEditing ? 'not-allowed' : 'pointer',
                    userSelect: 'none', display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <IcoClone size={14} style={{ color: hasExistingData ? '#9ca3af' : '#3b82f6' }} />
                  Same fee structure for all months (Uniform Annual Fees)
                </label>
                {uniformFees && !hasExistingData && (
                  <span style={{ marginLeft: 'auto', background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600 }}>
                    Active
                  </span>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Monthly fee cards */}
      {mode === 'manual' && !isLoadingInitial && contextReady && (
        localLoading ? (
          <div style={{ ...manualCard, textAlign: 'center', padding: 48 }}>
            <IcoSpinner size={48} style={{ color: '#2855ae', display: 'block', margin: '0 auto' }} />
            <p style={{ color: '#6b7280', marginTop: 16 }}>Loading existing data...</p>
          </div>
        ) : selectedClassId && currentClassData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {uniformFees ? (
              <div style={manualCard}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: 10, padding: 8, minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IcoCalendar size={14} style={{ color: 'white' }} />
                  </div>
                  <h3 style={{ color: '#2855ae', fontSize: 16, fontWeight: 600, margin: 0 }}>
                    Standard Monthly Fees (Applied to All Months)
                  </h3>
                  <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 14, fontWeight: 600 }}>
                    Per Month: ₹{currentClassData.monthlyFees[0]?.fees.reduce((s, f) => s + f.amount, 0).toLocaleString('en-IN') ?? 0}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  {(currentClassData.monthlyFees[0]?.fees ?? []).map((fee, feeIndex) => (
                    <div key={String(fee.id)} style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
                      <label style={{ display: 'block', color: '#374151', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                        {fee.fee_name}
                        {fee.optional === 1 && <span style={{ marginLeft: 6, fontSize: 10, color: '#6b7280' }}>(Optional)</span>}
                      </label>
                      <InputNumber
                        value={fee.amount}
                        onValueChange={e => updateFeeAmount(0, feeIndex, e.value ?? 0)}
                        mode="currency" currency="INR" locale="en-IN" min={0}
                        disabled={hasExistingData && !isEditing}
                        style={{ width: '100%' }}
                        inputStyle={{
                          width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
                          padding: '8px 10px', fontSize: 13,
                          backgroundColor: hasExistingData && !isEditing ? '#f3f4f6' : 'white',
                          cursor: hasExistingData && !isEditing ? 'not-allowed' : 'text',
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 16, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IcoInfo size={16} style={{ color: '#3b82f6' }} />
                  <span style={{ color: '#1e40af', fontSize: 13 }}>
                    These fee amounts will be applied uniformly to all months of the academic session.
                  </span>
                </div>
              </div>
            ) : (
              currentClassData.monthlyFees.map((monthFee, monthIndex) => (
                <div key={monthFee.monthYear} style={manualCard}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius: 10, padding: 8, minWidth: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 13, color: 'white', fontWeight: 700 }}>{monthIndex + 1}</span>
                    </div>
                    <h3 style={{ color: '#2855ae', fontSize: 16, fontWeight: 600, margin: 0 }}>{monthFee.month}</h3>
                    <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 14, fontWeight: 600 }}>
                      Total: ₹{monthFee.fees.reduce((s, f) => s + f.amount, 0).toLocaleString('en-IN')}
                    </span>
                    {isEditing && (
                      <button
                        onClick={() => handleDeleteMonth(monthFee.month, monthFee.monthYear)}
                        disabled={deletingMonth === monthFee.month}
                        style={{
                          marginLeft: 8, background: '#fee2e2', border: 'none', borderRadius: 7,
                          padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                          color: '#dc2626', fontWeight: 600, fontSize: 11,
                          opacity: deletingMonth === monthFee.month ? 0.6 : 1,
                        }}
                      >
                        {deletingMonth === monthFee.month
                          ? <IcoSpinner size={11} />
                          : <IcoTrash size={11} style={{ color: '#dc2626' }} />}
                        {deletingMonth === monthFee.month ? '...' : 'Delete'}
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                    {monthFee.fees.map((fee, feeIndex) => (
                      <div key={String(fee.id)} style={{ background: '#f9fafb', borderRadius: 8, padding: 12, border: '1px solid #e5e7eb' }}>
                        <label style={{ display: 'block', color: '#374151', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                          {fee.fee_name}
                          {fee.optional === 1 && <span style={{ marginLeft: 6, fontSize: 10, color: '#6b7280' }}>(Optional)</span>}
                        </label>
                        <InputNumber
                          value={fee.amount}
                          onValueChange={e => updateFeeAmount(monthIndex, feeIndex, e.value ?? 0)}
                          mode="currency" currency="INR" locale="en-IN" min={0}
                          disabled={hasExistingData && !isEditing}
                          style={{ width: '100%' }}
                          inputStyle={{
                            width: '100%', border: '1px solid #d1d5db', borderRadius: 6,
                            padding: '8px 10px', fontSize: 13,
                            backgroundColor: hasExistingData && !isEditing ? '#f3f4f6' : 'white',
                            cursor: hasExistingData && !isEditing ? 'not-allowed' : 'text',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}

            <div style={{ ...manualCard, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
              <p style={{ color: '#374151', fontSize: 14, margin: 0 }}>
                <strong>Total Annual Fee:</strong>{' '}
                <span style={{ color: '#2855ae', fontSize: 24, fontWeight: 700 }}>
                  ₹{totalFee.toLocaleString('en-IN')}
                </span>
              </p>
              <button
                onClick={handleSaveFees}
                disabled={saving || hasExistingData}
                className="fss-btn-primary"
                style={{
                  background: saving || hasExistingData ? '#9ca3af' : undefined,
                  boxShadow: saving || hasExistingData ? 'none' : undefined,
                  cursor: saving || hasExistingData ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? <IcoSpinner size={14} /> : <IcoCheck size={14} />}
                {saving ? 'Saving...' : 'Save Fee Structure'}
              </button>
            </div>
          </div>
        ) : null
      )}

      {/* ── Bulk Wizard ── */}
      {!isLoadingInitial && contextReady && mode === 'bulk' && (
        <div style={{ animation: 'fss-fadeUp 0.3s ease both' }}>
          <div style={{
            background: 'white', borderRadius: 16, padding: '14px 20px', marginBottom: 16,
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1.5px solid #e8edf5',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginRight: 8 }}>Steps:</span>
            {[
              { label: '1 · Select Scope', s: 0 },
              { label: '2 · Set Amounts', s: 1 },
              { label: '3 · Preview', s: 2 },
              { label: '4 · Result', s: 3 },
            ].map(({ label, s }) => (
              <span
                key={s}
                style={{
                  padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                  background: bulkStep === s ? '#1e3a8a' : bulkStep > s ? '#dcfce7' : '#f1f5f9',
                  color: bulkStep === s ? 'white' : bulkStep > s ? '#166534' : '#94a3b8',
                }}
              >
                {bulkStep > s && '✓ '}{label}
              </span>
            ))}
          </div>

          {bulkStep === 0 && (
            <>
              <div className="fss-bulk-card">
                <div className="fss-bulk-card-title">
                  <div className="fss-bicon"><IcoGradCap size={13} /></div>
                  Select Classes
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>
                    {bulkSelectedClasses.length} selected
                  </span>
                  <button
                    onClick={() => setBulkSelectedClasses((classes || []).map((c: any) => c.class_name))}
                    style={{ fontSize: 11, color: '#2855ae', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setBulkSelectedClasses([])}
                    style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}
                  >
                    Clear
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(classes || []).map((cls: any) => (
                    <div
                      key={String(cls.id)}
                      className={`fss-bcls-chip ${bulkSelectedClasses.includes(cls.class_name) ? 'active' : ''}`}
                      onClick={() => bulkToggleClass(cls.class_name)}
                    >
                      {cls.class_name}
                    </div>
                  ))}
                </div>
              </div>

              <div className="fss-bulk-card">
                <div className="fss-bulk-card-title">
                  <div className="fss-bicon"><IcoCalendar size={13} /></div>
                  Month Scope
                </div>
                <div className="fss-scope-toggle" style={{ marginBottom: 14 }}>
                  <button className={`fss-scope-btn ${bulkMonthScope === 'ALL' ? 'active' : ''}`} onClick={() => setBulkMonthScope('ALL')}>
                    All Months
                  </button>
                  <button className={`fss-scope-btn ${bulkMonthScope === 'specific' ? 'active' : ''}`} onClick={() => setBulkMonthScope('specific')}>
                    Specific Months
                  </button>
                </div>
                {bulkMonthScope === 'specific' ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                    {sessionMonths.map(sm => {
                      const label = `${sm.month} ${sm.year}`;
                      return (
                        <div
                          key={label}
                          className={`fss-bmonth-chip ${bulkSelectedMonths.includes(label) ? 'active' : ''}`}
                          onClick={() => bulkToggleMonth(label)}
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '9px 13px', fontSize: 12, color: '#1d4ed8', display: 'flex', gap: 7, alignItems: 'center' }}>
                    <IcoInfo size={13} />
                    All <strong>{sessionMonths.length}</strong> months of <strong>{displaySession}</strong> will be updated.
                  </div>
                )}
              </div>

              <div className="fss-bulk-card">
                <div className="fss-bulk-card-title">
                  <div className="fss-bicon"><IcoCalendarPlus size={13} /></div>
                  Year Scope
                </div>
                <div className="fss-scope-toggle" style={{ marginBottom: 12 }}>
                  <button className={`fss-scope-btn ${bulkYearScope === 'ALL' ? 'active' : ''}`} onClick={() => setBulkYearScope('ALL')}>
                    Session Years (Auto)
                  </button>
                  <button className={`fss-scope-btn ${bulkYearScope === 'specific' ? 'active' : ''}`} onClick={() => setBulkYearScope('specific')}>
                    Specific Year
                  </button>
                </div>
                {bulkYearScope === 'specific' ? (
                  <input
                    type="number"
                    value={bulkSpecificYear}
                    onChange={e => setBulkSpecificYear(e.target.value)}
                    placeholder="e.g. 2025"
                    style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '8px 13px', fontSize: 13, fontWeight: 600, outline: 'none', width: 160 }}
                  />
                ) : (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '9px 13px', fontSize: 12, color: '#15803d', display: 'flex', gap: 7, alignItems: 'center' }}>
                    <IcoCheckCircle size={13} />
                    Years derived automatically from <strong>{displaySession}</strong>.
                  </div>
                )}
              </div>

              <div className="fss-bulk-card">
                <div className="fss-bulk-card-title">
                  <div className="fss-bicon"><IcoSliders size={13} /></div>
                  Fee Entry Mode
                </div>
                <div className="fss-scope-toggle">
                  <button className={`fss-scope-btn ${bulkUniformFees ? 'active' : ''}`} onClick={() => setBulkUniformFees(true)}>
                    Same for all classes
                  </button>
                  <button className={`fss-scope-btn ${!bulkUniformFees ? 'active' : ''}`} onClick={() => setBulkUniformFees(false)}>
                    Per class amounts
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="fss-btn-primary"
                  disabled={
                    bulkSelectedClasses.length === 0 ||
                    (bulkMonthScope === 'specific' && bulkSelectedMonths.length === 0) ||
                    (bulkYearScope === 'specific' && !bulkSpecificYear)
                  }
                  onClick={bulkBuildRows}
                >
                  Set Amounts <IcoArrowRight size={14} />
                </button>
              </div>
            </>
          )}

          {bulkStep === 1 && (
            <>
              {bulkUniformFees ? (
                <div className="fss-bulk-card">
                  <div className="fss-bulk-card-title">
                    <div className="fss-bicon"><IcoWallet size={13} /></div>
                    Fee Amounts — Uniform for All Classes
                  </div>
                  {feeTypes.filter((ft: any) => ft.status === 'active').map((ft: any) => (
                    <div className="fss-bfee-row" key={String(ft.id)}>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#334155' }}>
                        {ft.fee_name}
                        {ft.optional === 1 && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>(Optional)</span>}
                      </span>
                      <InputNumber
                        value={bulkUniformAmounts[ft.fee_name] ?? 0}
                        onValueChange={e => setBulkUniformAmounts(p => ({ ...p, [ft.fee_name]: e.value ?? 0 }))}
                        mode="currency" currency="INR" locale="en-IN" min={0}
                        style={{ width: 180 }}
                        inputStyle={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}
                      />
                    </div>
                  ))}
                  <div style={{ marginTop: 14, padding: '11px 14px', background: '#f8fafc', borderRadius: 10, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>Monthly Total</span>
                    <span style={{ fontSize: 17, fontWeight: 800, color: '#1e3a8a' }}>
                      ₹{Object.values(bulkUniformAmounts).reduce((a, b) => a + b, 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              ) : (
                bulkSelectedClasses.map(cls => (
                  <div className="fss-bulk-card" key={cls}>
                    <div className="fss-bulk-card-title">
                      <div className="fss-bicon"><IcoGradCap size={13} /></div>
                      {cls}
                    </div>
                    {feeTypes.filter((ft: any) => ft.status === 'active').map((ft: any) => (
                      <div className="fss-bfee-row" key={String(ft.id)}>
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#334155' }}>
                          {ft.fee_name}
                          {ft.optional === 1 && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>(Optional)</span>}
                        </span>
                        <InputNumber
                          value={(bulkPerClassAmounts[cls]?.[ft.fee_name]) ?? (bulkUniformAmounts[ft.fee_name] ?? 0)}
                          onValueChange={e =>
                            setBulkPerClassAmounts(p => ({
                              ...p,
                              [cls]: { ...(p[cls] || bulkUniformAmounts), [ft.fee_name]: e.value ?? 0 },
                            }))
                          }
                          mode="currency" currency="INR" locale="en-IN" min={0}
                          style={{ width: 180 }}
                          inputStyle={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}
                        />
                      </div>
                    ))}
                  </div>
                ))
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button className="fss-btn-secondary" onClick={() => setBulkStep(0)}>
                  <IcoArrowLeft size={14} /> Back
                </button>
                <button className="fss-btn-primary" onClick={bulkApplyAndPreview}>
                  Preview <IcoArrowRight size={14} />
                </button>
              </div>
            </>
          )}

          {bulkStep === 2 && (
            <>
              <div className="fss-bulk-card">
                <div className="fss-bulk-card-title">
                  <div className="fss-bicon"><IcoTable size={13} /></div>
                  Preview — {bulkRows.length} rows to upload
                </div>
                <div style={{ overflowX: 'auto', borderRadius: 11, border: '1.5px solid #e8edf5' }}>
                  <table className="fss-preview-tbl">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Class</th>
                        <th>Session</th>
                        <th>Month</th>
                        <th>Year</th>
                        {feeTypes.filter((ft: any) => ft.status === 'active').map((ft: any) => (
                          <th key={String(ft.id)}>{ft.fee_name}</th>
                        ))}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkRows.map((row, i) => (
                        <tr key={row.id}>
                          <td style={{ color: '#94a3b8', fontSize: 11 }}>{i + 1}</td>
                          <td style={{ fontWeight: 700, color: '#1e293b' }}>{row.className}</td>
                          <td>{row.session}</td>
                          <td>{row.month}</td>
                          <td>{row.year}</td>
                          {feeTypes.filter((ft: any) => ft.status === 'active').map((ft: any) => (
                            <td
                              key={String(ft.id)}
                              style={{
                                color: (row.fees[ft.fee_name] || 0) > 0 ? '#15803d' : '#94a3b8',
                                fontFamily: 'monospace',
                              }}
                            >
                              {(row.fees[ft.fee_name] || 0) > 0
                                ? `₹${row.fees[ft.fee_name].toLocaleString('en-IN')}`
                                : '—'}
                            </td>
                          ))}
                          <td style={{ fontWeight: 700, color: '#1e3a8a', fontFamily: 'monospace' }}>
                            ₹{Object.values(row.fees).reduce((a, b) => a + b, 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: 14, display: 'flex', gap: 16, background: '#f8fafc', borderRadius: 11, padding: '12px 16px' }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>
                    <strong style={{ color: '#1e293b' }}>{bulkRows.length}</strong> rows &nbsp;·&nbsp;
                    <strong style={{ color: '#1e293b' }}>{bulkSelectedClasses.length}</strong> classes &nbsp;·&nbsp;
                    <strong style={{ color: '#1e293b' }}>{displaySession}</strong>
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button className="fss-btn-secondary" onClick={() => setBulkStep(1)}>
                  <IcoArrowLeft size={14} /> Back
                </button>
                <button className="fss-btn-primary" disabled={bulkUploading} onClick={handleBulkUpload}>
                  {bulkUploading
                    ? <><IcoSpinner size={14} /> Uploading...</>
                    : <><IcoCloud size={14} /> Upload Now</>}
                </button>
              </div>
            </>
          )}

          {bulkStep === 3 && (() => {
            const successCount = bulkResults.filter(r => r.status === 'success').length;
            const errorCount = bulkResults.filter(r => r.status === 'error').length;
            return (
              <>
                <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
                  {[
                    { num: successCount, lbl: 'Successful', bg: '#f0fdf4', border: '#bbf7d0', col: '#15803d' },
                    { num: errorCount, lbl: 'Errors', bg: '#fff1f2', border: '#fecdd3', col: '#be123c' },
                    { num: bulkResults.length, lbl: 'Total', bg: '#eff6ff', border: '#bfdbfe', col: '#1d4ed8' },
                  ].map(({ num, lbl, bg, border, col }) => (
                    <div
                      key={lbl}
                      style={{
                        flex: 1, background: bg, border: `1.5px solid ${border}`,
                        borderRadius: 14, padding: '16px 20px', textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: 30, fontWeight: 800, color: col, lineHeight: 1 }}>{num}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: col, marginTop: 4, opacity: 0.8 }}>{lbl}</div>
                    </div>
                  ))}
                </div>

                {errorCount > 0 && (
                  <div className="fss-bulk-card">
                    <div className="fss-bulk-card-title">
                      <div className="fss-bicon" style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}>
                        <IcoX size={13} />
                      </div>
                      Errors ({errorCount})
                    </div>
                    {bulkResults.filter(r => r.status === 'error').map((r, i) => (
                      <div className="fss-bresult-item error" key={i}>
                        <IcoWarn size={13} style={{ color: '#be123c', marginTop: 1, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 700, color: '#be123c', marginBottom: 2 }}>
                            Row {r.row}{r.fee_name ? ` — ${r.fee_name}` : ''}
                          </div>
                          <div style={{ color: '#64748b' }}>{r.message}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {successCount > 0 && (
                  <div className="fss-bulk-card">
                    <div className="fss-bulk-card-title">
                      <div className="fss-bicon" style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
                        <IcoCheck size={13} />
                      </div>
                      Successful ({successCount})
                    </div>
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                      {bulkResults.filter(r => r.status === 'success').map((r, i) => (
                        <div className="fss-bresult-item success" key={i}>
                          <IcoCheckCircle size={13} style={{ color: '#15803d', marginTop: 1, flexShrink: 0 }} />
                          <div style={{ flex: 1 }}>
                            <strong style={{ color: '#15803d' }}>{r.class_name}</strong>
                            <span style={{ color: '#94a3b8', margin: '0 5px' }}>·</span>
                            <span style={{ color: '#334155' }}>{r.fee_name}</span>
                            <span style={{ color: '#94a3b8', margin: '0 5px' }}>·</span>
                            <span style={{ color: '#64748b', fontSize: 11 }}>{r.month_year}</span>
                          </div>
                          <span style={{
                            fontWeight: 700, fontSize: 12, color: '#15803d',
                            background: '#dcfce7', padding: '2px 8px', borderRadius: 6,
                          }}>
                            ₹{(r.amount || 0).toLocaleString('en-IN')}
                          </span>
                          <span style={{
                            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                            color: r.action === 'inserted' ? '#1d4ed8' : '#7c3aed',
                            background: r.action === 'inserted' ? '#eff6ff' : '#f5f3ff',
                            padding: '2px 8px', borderRadius: 6, marginLeft: 4,
                          }}>
                            {r.action}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="fss-btn-primary" onClick={resetBulk}>
                    <IcoRefresh size={14} /> Start Over
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}